import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Header, PopUpMessage } from 'Components/Common';
import { Typography, Grid } from '@material-ui/core';
import { BottomContainerAction, ButtonActions, Loader } from 'Components/UI';
import axios from 'axios';
import {
  formattingDigitCurrency,
  getPayPlusData,
  postCanPayAmount,
  findGetParameter,
  postConfirmPay,
} from 'utils';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { Link, useHistory } from 'react-router-dom';
import { DEMO_ACCOUNT_ID } from 'config';

import {
  ticketAtom,
  goToPayAtom,
  amountToPayAtom,
  amountToPayPerProductAtom,
  shareNoteAtom,
  payPlusSessionAtom,
  stepPaymentTicketAtom,
  listProductsSelectedToPayAtom,
  paymentPendingValidationAtom,
  isIOSDeviceAtom,
  tipsAtom,
  feedbackCookingAtom,
  feedbackServiceAtom,
} from 'Atoms/Ticket';
import { staticDataAtom } from 'Atoms/Common';
import PayConfirmationSuccess from '../PayConfirmationSuccess';
import PayPlusForm from '../PayPlusForm';
import { useWindowSize } from 'Components/Hooks';
import useStyles from './styles';

const PaymentForm = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const [ticket] = useAtom(ticketAtom);
  const [staticData] = useAtom(staticDataAtom);
  const { id } = ticket;
  const { countryCode, labelCode } = staticData?.currency;
  const [amountToPay] = useAtom(amountToPayAtom);
  const [amountToPayPerProduct] = useAtom(amountToPayPerProductAtom);
  const [shareNote, setShareNote] = useAtom(shareNoteAtom);
  const [, setPaymentPendingValidation] = useAtom(paymentPendingValidationAtom);
  const [goToPay] = useAtom(goToPayAtom);
  const [totalAmount, setTotalAmount] = useState('');
  const [payPlusValidationForm, setPayPlusValidationForm] = useState(false);
  const [onFieldEdit, setOnFieldEdit] = useState(false);
  const [confirmationPaid, setConfirmationPaid] = useState(null);
  const [errorPaid, setErrorPaid] = useState(false);
  const [listProductsSelectedToPay] = useAtom(listProductsSelectedToPayAtom);
  const [stepPaymentTicket, setStepPaymentTicket] = useAtom(
    stepPaymentTicketAtom,
  );
  const [payPlusSession, setPayPlusSession] = useAtom(payPlusSessionAtom);
  const [loader, setLoader] = useState(false);
  const [isIOSDevice] = useAtom(isIOSDeviceAtom);
  const [feedbackCooking] = useAtom(feedbackCookingAtom);
  const [feedbackService] = useAtom(feedbackServiceAtom);
  const [processingPayment, setProcessingPayment] = useState(false);
  const size = useWindowSize();
  const [cannotPayAmount, setCannotPayAmount] = useState(false);
  const [errorLoadingPayPlus, setErrorLoadingPayPlus] = useState(false);
  const [newPayPlusToken, setNewPayPlusToken] = useState(false);
  const [oldAmountToPayBeforeReddirect, setOldAmountToPayBeforeRedirect] =
    useState(0);
  const [confirmCanPayStatus, setConfirmCanPayStatus] = useState(false);
  const [tips] = useAtom(tipsAtom);
  const [demoAccount, setDemoAccount] = useState(false);
  const [instance, setInstance] = useState(null);

  useEffect(() => {
    const pathname = window?.location?.search;
    if (pathname.includes('redirectFromPayment')) {
      setConfirmCanPayStatus(true);
      checkPaymentSecureIsValidadted(pathname);
    }
    if (!goToPay && !pathname.includes('redirectFromPayment')) {
      history.push(`/ticket/:${id}`);
    }
  }, []);

  useEffect(() => {
    const totalWithTips = tips > 0 ? amountToPay + tips : amountToPay;
    const total = formattingDigitCurrency(
      totalWithTips,
      countryCode,
      labelCode,
    );

    setTotalAmount(total);
  }, []);

  useEffect(() => {
    if (
      (!!confirmationPaid && !!payPlusValidationForm) ||
      (confirmationPaid && !!confirmationPaid)
    ) {
      setPaymentPendingValidation(true);
      setShareNote({ selected: null, value: null, condition: null });
      const timer = setTimeout(() => {
        redirect(`/ticket/:${id}?checkout`);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [confirmationPaid]);

  useEffect(() => {
    if (!!errorPaid && !!payPlusValidationForm) {
      setErrorPaid(false);
    }
  }, [payPlusValidationForm]);

  useEffect(() => {
    const pathname = window?.location?.search;
    const source = axios.CancelToken.source();
    if (
      !pathname.includes('redirectFromPayment') &&
      ticket?.id &&
      staticData?.idSession &&
      !errorPaid &&
      !payPlusValidationForm &&
      !newPayPlusToken
    ) {
      fetchPayPlusData(source).catch((err) => {
        console.log(err);
      });
    }
    if (!!processingPayment) {
      setPayPlusValidationForm(false);
    }

    return () => {
      source.cancel();
    };
  }, []);

  useEffect(() => {
    if (ticket?.id && staticData?.idSession && !!errorPaid) {
      fetchPayPlusData();
      setProcessingPayment(false);
      setTimeout(() => {
        setErrorPaid(false);
      }, 5000);
    }
  }, [errorPaid]);

  const fetchPayPlusData = async (source = null) => {
    const ticketAlias = ticket?.id;
    const idSession = staticData?.idSession;
    const selected = shareNote?.selected;
    const step = stepPaymentTicket ? stepPaymentTicket : 'checkout';
    const total = confirmCanPayStatus
      ? oldAmountToPayBeforeReddirect
      : amountToPay;
    const body = {
      step: step,
      type: selected,
      amount: selected === 'perProduct' ? amountToPayPerProduct : total,
      products: selected === 'perProduct' ? listProductsSelectedToPay : null,
      tips: tips,
      feedbacks: {
        cooking: feedbackCooking,
        service: feedbackService,
      },
    };

    if (body.amount && body.amount <= 0) {
      return setCannotPayAmount(true);
    }

    const response = await getPayPlusData(ticketAlias, idSession, body, source);

    if (response.account && response.account === DEMO_ACCOUNT_ID)
      return setDemoAccount(true);

    setPayPlusSession(response);
    setErrorLoadingPayPlus(
      !response?.idTransaction || !response?.token ? true : false,
    );
    setNewPayPlusToken(true);
  };

  const redirect = (to) => {
    setStepPaymentTicket('selected');
    setTimeout(() => history.push(to), 4500);
  };

  const reloadTpe = async (e) => {
    setErrorLoadingPayPlus(false);
    setTimeout(() => {
      fetchPayPlusData();
    }, 2000);
  };

  const labelButtonAction = shareNote?.selected?.length
    ? `${t('pay_my_part')} (${totalAmount})`
    : `${t('pay_bill')} (${totalAmount})`;

  const submitCanPay = async (e) => {
    e.preventDefault();
    const ticketAlias = ticket?.id;
    const idSession = staticData?.idSession;
    const total = confirmCanPayStatus
      ? oldAmountToPayBeforeReddirect
      : amountToPay;
    const amount = Math.round(total * 100) / 100;
    if (amount <= 0 && tips <= 0) {
      return setCannotPayAmount(true);
    }
    const postCanPay = await postCanPayAmount(
      amount,
      payPlusSession?.idTransaction,
      idSession,
      ticketAlias,
      tips,
    );
    if (!!postCanPay?.canPay) {
      submitPayPlus();
      setProcessingPayment(true);
    } else {
      setCannotPayAmount(true);
    }
  };
  const submitPayPlus = () => {
    const { token, session } = payPlusSession;
    setProcessingPayment(true);
    instance.do({ action: 'submit' });
  };

  const checkPaymentSecureIsValidadted = async (pathname) => {
    const body = findGetParameter(pathname);
    const ticketAlias = ticket?.id;
    const idSession = staticData?.idSession;
    const submit = await postConfirmPay(body, idSession, ticketAlias);
    const amount = submit?.amount ? submit.amount : amountToPay;
    const total = formattingDigitCurrency(amount, countryCode, labelCode);

    if (submit?.ok) {
      setConfirmationPaid(true);
      setPayPlusValidationForm(true);
      setTotalAmount(total);
      setPayPlusSession({ ...payPlusSession, idTransaction: body.id });
    } else {
      setErrorPaid(true);
      setOldAmountToPayBeforeRedirect(amount);
      setTotalAmount(total);
      reloadTpe();
    }
  };

  const handleClose = () => {
    setCannotPayAmount(false);
  };

  return (
    <div className={`${isIOSDevice ? classes.iOS : classes.Android}`}>
      <Header
        title={t('my_ticket')}
        actions={{
          prev: !processingPayment
            ? { icon: 'cancel', label: t('cancel'), path: `/ticket/:${id}` }
            : {},
        }}
      ></Header>
      {confirmationPaid && payPlusValidationForm && (
        <PayConfirmationSuccess totalAmount={totalAmount} />
      )}

      {(!confirmationPaid || (confirmationPaid && !payPlusValidationForm)) && (
        <>
          <Grid
            container
            className={`${classes.root} ${
              isIOSDevice && /* size.height <= 500 &&  */ classes.iOSFullheight
            } ${isIOSDevice && size?.height <= 500 && classes.iOSMargin}`}
            justifyContent="space-around"
            alignContent="center"
          >
            <Grid
              item
              className={`${
                isIOSDevice ? classes.payPlusFormIOS : classes.payPlusForm
              }`}
            >
              <Grid container justifyContent="flex-start" direction="column">
                <Grid item className={classes.restToPay}>
                  <Typography align="center" className={classes.subTitle}>
                    {t('to_pay')}
                  </Typography>
                  <Typography align="center" className={classes.title}>
                    {totalAmount}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  className={`${!!errorPaid ? classes.visible : classes.hide}`}
                >
                  {!!errorPaid && !onFieldEdit && (
                    <div className={`${classes.error}`}>
                      <Typography>{t('payment_refused')}</Typography>
                    </div>
                  )}
                </Grid>

                {confirmationPaid && !payPlusValidationForm && (
                  <Grid item xs={12} className={classes.error}>
                    <Typography className={classes.error}>
                      {t('warning_already_paid')}
                    </Typography>
                  </Grid>
                )}
                {!!loader && <Loader />}
                {payPlusSession?.session && !confirmationPaid && !loader && (
                  <div className={!isIOSDevice ? classes.minHeight : ''}>
                    <PayPlusForm
                      setPayPlusValidationForm={setPayPlusValidationForm}
                      onFieldEdit={onFieldEdit}
                      setOnFieldEdit={setOnFieldEdit}
                      setConfirmationPaid={setConfirmationPaid}
                      setErrorPaid={setErrorPaid}
                      loader={loader}
                      setLoader={setLoader}
                      payPlusValidationForm={payPlusValidationForm}
                      setNewPayPlusToken={setNewPayPlusToken}
                      setInstance={setInstance}
                    />
                  </div>
                )}
              </Grid>
            </Grid>
            {isIOSDevice ? (
              <BottomContainerAction hide={!!processingPayment ? true : false}>
                <Link
                  to={{}}
                  onClick={(e) => submitCanPay(e)}
                  className={!payPlusValidationForm ? classes.disabled : ''}
                >
                  {payPlusValidationForm ? (
                    <button className={classes.buttonPay}>
                      {labelButtonAction}
                    </button>
                  ) : (
                    <ButtonActions
                      label={labelButtonAction}
                      color="default"
                      styles={{
                        background: 'backgroundGrey',
                        label: payPlusValidationForm
                          ? 'labelWhite'
                          : 'labelGrey',
                      }}
                    />
                  )}
                </Link>
              </BottomContainerAction>
            ) : (
              ''
            )}
          </Grid>
          {!isIOSDevice ? (
            <BottomContainerAction hide={!!processingPayment ? true : false}>
              <Link
                to={{}}
                onClick={(e) => submitCanPay(e)}
                className={!payPlusValidationForm ? classes.disabled : ''}
              >
                {payPlusValidationForm ? (
                  <button className={classes.buttonPay}>
                    {labelButtonAction}
                  </button>
                ) : (
                  <ButtonActions
                    label={labelButtonAction}
                    color="default"
                    styles={{
                      background: 'backgroundGrey',
                      label: payPlusValidationForm ? 'labelWhite' : 'labelGrey',
                    }}
                  />
                )}
              </Link>
            </BottomContainerAction>
          ) : (
            ''
          )}
        </>
      )}

      <PopUpMessage
        open={errorLoadingPayPlus}
        handleClose={handleClose}
        title={t('warning')}
        content={t('warning_error_tpe')}
      >
        {
          <Link to={{}} onClick={(e) => reloadTpe(e)}>
            <ButtonActions
              label={t('reload_tpe')}
              styles={{
                label: 'labelWhite',
                radius: 'radius33',
              }}
              color="secondary"
            />
          </Link>
        }
      </PopUpMessage>
      <PopUpMessage
        open={demoAccount}
        handleClose={handleClose}
        title={t('warning')}
        content={t('demo_account')}
      >
        <Link to={{ pathname: `/ticket/:${id}?errorPay` }}>
          <ButtonActions
            label={t('reload_ticket')}
            styles={{
              label: 'labelWhite',
              radius: 'radius33',
            }}
            color="secondary"
          />
        </Link>
      </PopUpMessage>
      <PopUpMessage
        open={cannotPayAmount}
        handleClose={handleClose}
        title={t('warning')}
        content={t('cannot_pay')}
      >
        <Link to={{ pathname: `/ticket/:${id}` }}>
          <ButtonActions
            label={t('reload_order')}
            styles={{
              label: 'labelWhite',
              radius: 'radius33',
            }}
            color="secondary"
          />
        </Link>
      </PopUpMessage>
    </div>
  );
};

PaymentForm.propTypes = {};

export default PaymentForm;
